import { pixi } from './alias'
import { getColorFromList, Theme, getSecondaryColor, getPatternForIndex } from './color'
import { Layout } from './layout'
import { Direction, Station } from './type'
import { directionToDelta } from './util/direction'

export function createSketcher(layout: Layout, theme: Theme, showColorIndices: boolean) {
  const text = (textContent: string, fontSize: number) => {
    return new pixi.Text(textContent, {
      fontFamily: 'Arial',
      fontSize, // layout.squareWidth / 4
      fill: 0xffffff,
      // dropShadow: true,
      // dropShadowDistance: 0,
      // dropShadowBlur: 2,
    })
  }

  const roadTurn = () => {
    let g = new pixi.Graphics()
    // interior road hole
    const hole = () => {
      g.moveTo(0, 0)
      g.arc(0, 0, layout.roadHole, 0, Math.PI / 2 - 0.0001, false)
      g.cut()
    }

    // road turn exterior
    g.moveTo(0, 0)
    g.arc(0, 0, layout.roadHole + layout.roadWidth, 0, pixi.PI_2 / 4, false)
    g.fill(theme.rail)
    hole()
    // road turn center
    g.moveTo(0, 0)
    g.arc(
      0,
      0,
      layout.roadHole + layout.roadBorder + layout.interiorRoadWidth,
      0,
      pixi.PI_2 / 4,
      false,
    )
    g.fill(theme.railInner)
    hole()
    // road turn interior
    g.moveTo(0, 0)
    g.arc(0, 0, layout.roadHole + layout.roadBorder, 0, pixi.PI_2 / 4, false)
    g.fill(theme.rail)
    hole()

    return g
  }

  const road = () => {
    let g = new pixi.Graphics()
    // road exterior
    g.moveTo(0, 0)
    g.rect(0, layout.roadHole, layout.squareWidth, layout.roadWidth)
    g.fill(theme.rail)

    // road interior
    g.moveTo(0, 0)
    g.rect(0, layout.roadHole + layout.roadBorder, layout.squareWidth, layout.interiorRoadWidth)
    g.fill(theme.railInner)

    return g
  }

  // Draw a pattern marker to help distinguish colors at higher levels
  const drawPatternMarker = (g: pixi.Graphics, cx: number, cy: number, markerSize: number, pattern: string) => {
    const markerColor = 0xffffff
    const s = markerSize
    
    switch (pattern) {
      case 'stripe':
        // Horizontal stripe
        g.roundRect(cx - s * 0.8, cy - s * 0.15, s * 1.6, s * 0.3, s * 0.1)
        g.fill(markerColor)
        break
      case 'dot':
        // Center dot
        g.circle(cx, cy, s * 0.35)
        g.fill(markerColor)
        break
      case 'ring':
        // Ring outline
        g.circle(cx, cy, s * 0.5)
        g.fill(markerColor)
        g.circle(cx, cy, s * 0.3)
        g.cut()
        break
      case 'diamond':
        // Diamond shape
        g.moveTo(cx, cy - s * 0.5)
        g.lineTo(cx + s * 0.4, cy)
        g.lineTo(cx, cy + s * 0.5)
        g.lineTo(cx - s * 0.4, cy)
        g.closePath()
        g.fill(markerColor)
        break
      case 'cross':
        // Cross/plus
        g.roundRect(cx - s * 0.12, cy - s * 0.5, s * 0.24, s * 1.0, s * 0.05)
        g.fill(markerColor)
        g.roundRect(cx - s * 0.5, cy - s * 0.12, s * 1.0, s * 0.24, s * 0.05)
        g.fill(markerColor)
        break
      case 'triangle':
        // Triangle marker
        g.moveTo(cx, cy - s * 0.45)
        g.lineTo(cx + s * 0.4, cy + s * 0.35)
        g.lineTo(cx - s * 0.4, cy + s * 0.35)
        g.closePath()
        g.fill(markerColor)
        break
      case 'square':
        // Square marker
        g.roundRect(cx - s * 0.35, cy - s * 0.35, s * 0.7, s * 0.7, s * 0.08)
        g.fill(markerColor)
        break
      // 'solid' or default - no marker needed
    }
  }

  // Draw a house icon for stations (destinations)
  const drawHouse = (g: pixi.Graphics, cx: number, cy: number, size: number, primaryColor: number, secondaryColor: number | null, isDeparture: boolean, colorIndex: number) => {
    const houseWidth = size * 0.75
    const houseHeight = size * 0.5
    const roofHeight = size * 0.35
    
    // House body (rectangle)
    const bodyLeft = cx - houseWidth / 2
    const bodyTop = cy - houseHeight / 2 + roofHeight / 2
    const bodyBottom = cy + houseHeight / 2 + roofHeight / 2
    
    // Draw house body with rounded corners
    g.roundRect(bodyLeft, bodyTop, houseWidth, houseHeight, size * 0.05)
    g.fill(primaryColor)
    
    // Draw roof (triangle)
    const roofTop = cy - houseHeight / 2 - roofHeight / 2 + roofHeight / 4
    const roofLeft = cx - houseWidth / 2 - size * 0.08
    const roofRight = cx + houseWidth / 2 + size * 0.08
    
    g.moveTo(cx, roofTop)
    g.lineTo(roofRight, bodyTop + size * 0.02)
    g.lineTo(roofLeft, bodyTop + size * 0.02)
    g.closePath()
    g.fill(secondaryColor !== null ? secondaryColor : darkenColor(primaryColor, 0.15))
    
    // Draw door (only for non-departure)
    if (!isDeparture) {
      const doorWidth = houseWidth * 0.25
      const doorHeight = houseHeight * 0.5
      const doorLeft = cx - doorWidth / 2
      const doorTop = bodyBottom - doorHeight
      
      g.roundRect(doorLeft, doorTop, doorWidth, doorHeight, size * 0.02)
      g.fill(darkenColor(primaryColor, 0.3))
    }
    
    // Draw pattern marker for higher color indices (helps distinguish similar colors)
    const pattern = getPatternForIndex(colorIndex)
    if (pattern !== 'solid' && !isDeparture) {
      const markerCx = cx
      const markerCy = bodyTop + houseHeight * 0.35
      drawPatternMarker(g, markerCx, markerCy, size * 0.25, pattern)
    }
  }

  const station = (stationData: Station, colorIndex: number, colorList: number[]) => {
    let direction: Direction
    let isDeparture = stationData.type === 'departure'
    if (stationData.type === 'departure') {
      direction = stationData.exit
    } else {
      direction = stationData.entrance
    }

    let { squareWidth, stationMargin } = layout

    let g = new pixi.Graphics()
    
    const cx = squareWidth / 2
    const cy = squareWidth / 2
    const size = squareWidth - 2 * stationMargin
    
    const primaryColor = getColorFromList(colorIndex, colorList)
    // For destinations, check if we should use dual colors
    const secondaryColor = !isDeparture && colorIndex > 0 ? getSecondaryColor(colorIndex, colorList) : null
    
    // Draw white background circle/base
    g.circle(cx, cy, size / 2 + 4)
    g.fill(0xffffff)
    
    // Draw the house
    drawHouse(g, cx, cy, size, primaryColor, secondaryColor, isDeparture, colorIndex)
    
    let { dx, dy } = directionToDelta(direction)

    let h = new pixi.Container()
    h.x += stationMargin * dx
    h.y += stationMargin * dy
    h.addChild(g)

    if (showColorIndices) {
      let textContent = colorIndex.toString(16).toUpperCase()
      let j = text(textContent, squareWidth / 4)
      j.x += 0.52 * squareWidth - 0.08 * squareWidth * textContent.length
      j.y += 0.38 * squareWidth
      h.addChild(j)
    }

    return h
  }

  // Draw a train icon
  const drawTrainIcon = (g: pixi.Graphics, cx: number, cy: number, size: number, primaryColor: number, secondaryColor: number | null, colorIndex: number) => {
    const trainWidth = size * 0.7
    const trainHeight = size * 0.45
    
    // Train body (rounded rectangle)
    const bodyLeft = cx - trainWidth / 2
    const bodyTop = cy - trainHeight / 2
    
    // Main train body
    g.roundRect(bodyLeft, bodyTop, trainWidth, trainHeight, size * 0.12)
    g.fill(primaryColor)
    
    // Train front (locomotive nose)
    const noseWidth = trainWidth * 0.15
    g.roundRect(bodyLeft + trainWidth - size * 0.02, bodyTop + trainHeight * 0.15, noseWidth, trainHeight * 0.7, size * 0.05)
    g.fill(primaryColor)
    
    // Windows (lighter area at top)
    const windowHeight = trainHeight * 0.35
    const windowTop = bodyTop + trainHeight * 0.12
    const windowMargin = trainWidth * 0.1
    
    if (secondaryColor !== null) {
      // Double color: use secondary color for window area
      g.roundRect(bodyLeft + windowMargin, windowTop, trainWidth - 2 * windowMargin, windowHeight, size * 0.05)
      g.fill(secondaryColor)
    } else {
      // Single color: lighter windows
      g.roundRect(bodyLeft + windowMargin, windowTop, trainWidth - 2 * windowMargin, windowHeight, size * 0.05)
      g.fill(lightenColor(primaryColor, 0.4))
    }
    
    // Wheels
    const wheelRadius = size * 0.08
    const wheelY = cy + trainHeight / 2 - wheelRadius * 0.3
    
    g.circle(cx - trainWidth * 0.25, wheelY, wheelRadius)
    g.fill(0x333333)
    g.circle(cx + trainWidth * 0.1, wheelY, wheelRadius)
    g.fill(0x333333)
    g.circle(cx + trainWidth * 0.35, wheelY, wheelRadius)
    g.fill(0x333333)
    
    // Chimney/smokestack
    const chimneyWidth = size * 0.1
    const chimneyHeight = size * 0.15
    g.roundRect(cx - trainWidth * 0.15, bodyTop - chimneyHeight + size * 0.02, chimneyWidth, chimneyHeight, size * 0.02)
    g.fill(darkenColor(primaryColor, 0.2))
    
    // Draw pattern marker for higher color indices
    const pattern = getPatternForIndex(colorIndex)
    if (pattern !== 'solid') {
      const markerCx = cx - trainWidth * 0.05
      const markerCy = cy + trainHeight * 0.05
      drawPatternMarker(g, markerCx, markerCy, size * 0.2, pattern)
    }
  }

  const train = (colorIndex: number, colorList: number[]) => {
    let { squareWidth } = layout
    let g = new pixi.Graphics()
    
    const cx = squareWidth / 2
    const cy = squareWidth / 2
    const size = layout.roadWidth * 1.8
    
    const primaryColor = getColorFromList(colorIndex, colorList)
    const secondaryColor = getSecondaryColor(colorIndex, colorList)
    
    // Draw white outline/background
    g.circle(cx, cy, size / 2 + 3)
    g.fill(0xffffff)
    
    // Draw the train icon
    drawTrainIcon(g, cx, cy, size, primaryColor, secondaryColor, colorIndex)
    
    let h = new pixi.Container()
    h.addChild(g)

    if (showColorIndices) {
      let textContent = colorIndex.toString(16).toUpperCase()
      let j = text(textContent, squareWidth / 7)
      j.x += 0.53 * squareWidth - 0.06 * squareWidth * textContent.length
      j.y += 0.42 * squareWidth
      h.addChild(j)
    }
    return h
  }

  const switchCircle = (g: pixi.Graphics, circleColor: number) => {
    g.clear()
    g.circle(layout.squareWidth / 2, layout.squareWidth / 2, layout.switchRadius)
    g.fill(theme.switchOutline)
    g.circle(
      layout.squareWidth / 2,
      layout.squareWidth / 2,
      layout.switchRadius - layout.switchOutlineWidth,
    )
    g.fill(circleColor)
  }

  return {
    roadTurn,
    road,
    station,
    train,
    switchCircle,
  }
}

// Helper function to darken a color
function darkenColor(color: number, amount: number): number {
  const r = Math.max(0, ((color >> 16) & 0xff) * (1 - amount))
  const g = Math.max(0, ((color >> 8) & 0xff) * (1 - amount))
  const b = Math.max(0, (color & 0xff) * (1 - amount))
  return (Math.round(r) << 16) | (Math.round(g) << 8) | Math.round(b)
}

// Helper function to lighten a color
function lightenColor(color: number, amount: number): number {
  const r = Math.min(255, ((color >> 16) & 0xff) + (255 - ((color >> 16) & 0xff)) * amount)
  const g = Math.min(255, ((color >> 8) & 0xff) + (255 - ((color >> 8) & 0xff)) * amount)
  const b = Math.min(255, (color & 0xff) + (255 - (color & 0xff)) * amount)
  return (Math.round(r) << 16) | (Math.round(g) << 8) | Math.round(b)
}

export type Sketcher = ReturnType<typeof createSketcher>
